import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, Plus, Trash2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { API } from '../App';

const enconOrange = '#ee7203';
export const MS_FORM_URL = 'https://forms.cloud.microsoft/pages/responsepage.aspx?id=PlJxjHhvUUWoFOzwm7bCYvnxI1KoRlxDgZcBd-q25DhUNVcxOUpEMkpIUlhMM0xPUzYwNlVJQ0cyMC4u&origin=lprLink&route=shorturl';

const pctDisplay = (v) => (v === null || v === undefined || v === '') ? '' : (v * 100).toFixed(1);
const BLANK_MANUAL_LINE = { product_code: '', description: '', supplier_code: '', group: '', type: 'Other', support_pct_display: '' };

/** Order Book "H/O £" pill modal — lets the user pick which of this order's own
 * product lines to add to the New Claims queue (pre-matched to the Supplier
 * Matrix, with a suggested % already filled in), plus add extra manual lines
 * for products ordered/might be ordered that weren't picked up automatically.
 * Submitting adds to New Claims AND opens the H/O Support MS Form in a new tab. */
export default function AddHOSupportClaimModal({ isOpen, orderNumber, onClose, onAdded }) {
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState(null);
  const [lineState, setLineState] = useState({}); // index -> { checked, supplier_code, group, type, support_pct_display }
  const [manualLines, setManualLines] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen || !orderNumber) return;
    setLoading(true);
    setManualLines([]);
    api.get(`${API}/order-book-reports/ho-support/order-claim-info/${orderNumber}`)
      .then((res) => {
        const data = res.data;
        setInfo(data);
        const initial = {};
        (data.lines || []).forEach((l, i) => {
          initial[i] = {
            checked: !!l.group && !l.already_tracked,
            supplier_code: l.supplier_code || '',
            group: l.group || '',
            type: 'Other',
            support_pct_display: pctDisplay(l.suggested_support_pct),
          };
        });
        setLineState(initial);
      })
      .catch(() => { setInfo(null); toast.error('Failed to load this order'); })
      .finally(() => setLoading(false));
  }, [isOpen, orderNumber]);

  const updateLine = (i, field, value) => setLineState((prev) => ({ ...prev, [i]: { ...prev[i], [field]: value } }));

  const addManualLine = () => setManualLines((prev) => [...prev, { ...BLANK_MANUAL_LINE }]);
  const updateManualLine = (i, field, value) => setManualLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)));
  const removeManualLine = (i) => setManualLines((prev) => prev.filter((_, idx) => idx !== i));

  const handleSubmit = async () => {
    if (!info?.account_number) { toast.error('No account found for this order'); return; }
    const selectedOrderLines = (info.lines || [])
      .map((l, i) => ({ line: l, state: lineState[i] }))
      .filter((x) => x.state?.checked);
    const validManualLines = manualLines.filter((l) => l.supplier_code && l.group);

    if (selectedOrderLines.length === 0 && validManualLines.length === 0) {
      toast.error('Select at least one line, or add a manual line with Supplier + Group');
      return;
    }

    const lines = [
      ...selectedOrderLines.map(({ line, state }) => ({
        account_number: info.account_number,
        customer_name: info.customer_name,
        supplier_code: state.supplier_code,
        group: state.group,
        type: state.type,
        support_pct: state.support_pct_display === '' ? null : parseFloat(state.support_pct_display) / 100,
        product_code: line.product_code,
        description: line.description,
      })),
      ...validManualLines.map((l) => ({
        account_number: info.account_number,
        customer_name: info.customer_name,
        supplier_code: l.supplier_code,
        group: l.group,
        type: l.type,
        support_pct: l.support_pct_display === '' ? null : parseFloat(l.support_pct_display) / 100,
        product_code: l.product_code || null,
        description: l.description || null,
      })),
    ];

    setSubmitting(true);
    try {
      await api.post(`${API}/order-book-reports/ho-support/claims/from-order`, { order_number: orderNumber, lines });
      toast.success(`Added ${lines.length} line(s) to New Claims`);
      onAdded();
      onClose();
      window.open(MS_FORM_URL, '_blank');
    } catch {
      toast.error('Failed to add claim lines');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-3xl" data-testid="ho-support-claim-modal">
        <DialogHeader>
          <DialogTitle>Add H/O Support Claim — Order #{orderNumber}</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" style={{ color: enconOrange }} /></div>
        ) : !info ? (
          <div className="py-10 text-center text-gray-400 text-sm">Couldn't load this order.</div>
        ) : (
          <div className="space-y-4">
            <div className="text-sm text-gray-700" data-testid="ho-support-claim-account-summary">
              <span className="font-mono font-semibold">{info.account_number || '—'}</span>
              {info.customer_name && <span className="text-gray-500 ml-2">— {info.customer_name}</span>}
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
                Order lines ({(info.lines || []).length})
              </label>
              {(info.lines || []).length === 0 ? (
                <div className="text-xs text-gray-400 border border-gray-200 rounded-lg px-3 py-3">
                  No product-level detail found for this order — use "Add manual line" below.
                </div>
              ) : (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 text-gray-500 uppercase text-[10px]">
                        <th className="text-left py-2 px-2 w-6"></th>
                        <th className="text-left py-2 px-2">Product</th>
                        <th className="text-left py-2 px-2">Supplier</th>
                        <th className="text-left py-2 px-2">Group</th>
                        <th className="text-left py-2 px-2">Type</th>
                        <th className="text-left py-2 px-2">Support %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(info.lines || []).map((l, i) => {
                        const st = lineState[i] || {};
                        return (
                          <tr key={i} className="border-t border-gray-100" data-testid={`ho-support-claim-line-${i}`}>
                            <td className="px-2 py-1.5">
                              <input type="checkbox" checked={!!st.checked} onChange={(e) => updateLine(i, 'checked', e.target.checked)}
                                className="w-4 h-4 accent-orange-500 cursor-pointer" data-testid={`ho-support-claim-line-checkbox-${i}`} />
                            </td>
                            <td className="px-2 py-1.5">
                              <div className="font-mono text-gray-700">{l.product_code || '—'}</div>
                              <div className="text-gray-400 truncate max-w-[160px]">{l.description}</div>
                              {l.already_tracked && <div className="text-green-600 text-[10px] font-semibold mt-0.5">Already tracked</div>}
                            </td>
                            <td className="px-2 py-1.5"><input type="text" value={st.supplier_code || ''} onChange={(e) => updateLine(i, 'supplier_code', e.target.value)} className="border border-gray-300 rounded px-1.5 py-1 text-xs w-20" /></td>
                            <td className="px-2 py-1.5"><input type="text" value={st.group || ''} onChange={(e) => updateLine(i, 'group', e.target.value)} className="border border-gray-300 rounded px-1.5 py-1 text-xs w-16" /></td>
                            <td className="px-2 py-1.5">
                              <select value={st.type || 'Other'} onChange={(e) => updateLine(i, 'type', e.target.value)} className="border border-gray-300 rounded px-1.5 py-1 text-xs">
                                <option value="Other">Other</option>
                                <option value="PIR">PIR</option>
                              </select>
                            </td>
                            <td className="px-2 py-1.5"><input type="number" step="0.1" value={st.support_pct_display || ''} onChange={(e) => updateLine(i, 'support_pct_display', e.target.value)} placeholder="%" className="border border-gray-300 rounded px-1.5 py-1 text-xs w-16" /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Manual lines</label>
                <button onClick={addManualLine} data-testid="ho-support-claim-add-manual-line" className="flex items-center gap-1 text-xs font-semibold" style={{ color: enconOrange }}>
                  <Plus className="w-3.5 h-3.5" /> Add manual line
                </button>
              </div>
              {manualLines.length > 0 && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <tbody>
                      {manualLines.map((l, i) => (
                        <tr key={i} className="border-t border-gray-100 first:border-t-0" data-testid={`ho-support-claim-manual-line-${i}`}>
                          <td className="px-2 py-1.5"><input type="text" value={l.description} onChange={(e) => updateManualLine(i, 'description', e.target.value)} placeholder="Description / product ordered or might order" className="border border-gray-300 rounded px-1.5 py-1 text-xs w-full" /></td>
                          <td className="px-2 py-1.5"><input type="text" value={l.supplier_code} onChange={(e) => updateManualLine(i, 'supplier_code', e.target.value)} placeholder="Supplier" className="border border-gray-300 rounded px-1.5 py-1 text-xs w-20" /></td>
                          <td className="px-2 py-1.5"><input type="text" value={l.group} onChange={(e) => updateManualLine(i, 'group', e.target.value)} placeholder="Group" className="border border-gray-300 rounded px-1.5 py-1 text-xs w-16" /></td>
                          <td className="px-2 py-1.5">
                            <select value={l.type} onChange={(e) => updateManualLine(i, 'type', e.target.value)} className="border border-gray-300 rounded px-1.5 py-1 text-xs">
                              <option value="Other">Other</option>
                              <option value="PIR">PIR</option>
                            </select>
                          </td>
                          <td className="px-2 py-1.5"><input type="number" step="0.1" value={l.support_pct_display} onChange={(e) => updateManualLine(i, 'support_pct_display', e.target.value)} placeholder="%" className="border border-gray-300 rounded px-1.5 py-1 text-xs w-16" /></td>
                          <td className="px-2 py-1.5"><button onClick={() => removeManualLine(i)} className="text-gray-400 hover:text-red-500" data-testid={`ho-support-claim-remove-manual-line-${i}`}><Trash2 className="w-3.5 h-3.5" /></button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={onClose} data-testid="ho-support-claim-cancel-btn">Cancel</Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="text-white"
                style={{ backgroundColor: enconOrange }}
                data-testid="ho-support-claim-submit-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <ExternalLink className="w-4 h-4 mr-1.5" />}
                Add to New Claims &amp; Open Form
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
